import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, Mail, Pencil, Loader2, ArrowLeft, Merge } from "lucide-react";
import { api, errMsg, downloadFile, fmtDate, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Empty } from "@/components/admin/ui";
import { StatusBadge } from "@/components/StatusBadge";
import { Tabs, TabsList, TabsTrigger, TabsContent, Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/typed";
import { OrgFormDialog } from "./Organizations";
import { DioceseContacts } from "@/components/admin/DioceseContacts";
import { CodesTable } from "./Codes";

const COUNT_TONES: [string, string, string][] = [["unused", "Unused", "text-[#15803D]"], ["reserved", "In use", "text-[#B45309]"], ["used", "Used", "text-[#1B365D]"], ["expired", "Expired", "text-[#64748B]"], ["voided", "Voided", "text-[#B91C1C]"]];

function MergeDialog({ o, onClose }: { o: any; onClose: () => void }) {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState<any[]>([]);
  const [into, setInto] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/organizations").then((r) => setOrgs(r.data.filter((x: any) => x.id !== o.id && x.id !== o.parent_org_id && x.parent_org_id !== o.id))); }, [o]);
  const target = orgs.find((x) => x.id === into);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/organizations/${o.id}/merge`, { into_id: into });
      toast.success(`Merged into ${data.into}: ${data.applicants} applicant(s), ${data.codes} code(s), ${data.orders} order(s) moved`);
      navigate(`/admin/organizations/${into}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) { onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="org-merge-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">Merge {o.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-[#64748B]">Moves all applicants, codes, orders, contacts and parishes from <b className="text-[#1A2536]">{o.name}</b> into the organization you pick, then removes {o.name}. This can't be undone.</p>
        <label className="block"><span className="field-label">Merge into</span>
          <select className="field-input" value={into} onChange={(e) => setInto(e.target.value)} data-testid="org-merge-select">
            <option value="">Choose the organization to keep…</option>
            {orgs.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        {target && <p className="rounded-xl bg-[#FFF1CC] px-3 py-2 text-xs text-[#7C4A03]" data-testid="org-merge-note"><b>{target.name}</b> will be kept. <b>{o.name}</b> will be removed after everything moves over.</p>}
        <div className="flex justify-end gap-2">
          <button className="btn-secondary btn-sm" onClick={onClose} data-testid="org-merge-cancel">Cancel</button>
          <button className="btn-primary btn-sm" disabled={!into || busy} onClick={go} data-testid="org-merge-confirm">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Merge className="h-4 w-4" />} Merge</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OrgTracker() {
  const { id } = useParams();
  const { can, user } = useAuth();
  const [merging, setMerging] = useState(false);
  const [d, setD] = useState<any>(null);
  const [edit, setEdit] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const load = useCallback(() => api.get(`/admin/organizations/${id}`).then((r) => setD(r.data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="h-40 animate-pulse rounded-2xl bg-white" />;
  const o = d.organization;

  const emailUnused = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/admin/organizations/${id}/email-unused-codes`);
      toast.success(`Emailed ${data.count} unused codes to ${data.to}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setEmailing(false);
  };

  return (
    <div>
      <Link to="/admin/organizations" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-[#64748B] hover:text-[#1B365D]"><ArrowLeft className="h-4 w-4" /> Organizations</Link>
      <PageHeader
        title={o.name}
        subtitle={[o.contact_name, o.email, o.phone].filter(Boolean).join(" · ")}
        actions={
          <>
            {can("manage_organizations") && <button className="btn-secondary btn-sm" onClick={() => setEdit(true)} data-testid="org-edit-button"><Pencil className="h-4 w-4" /> Edit</button>}
            {user?.role === "admin" && <button className="btn-secondary btn-sm" onClick={() => setMerging(true)} data-testid="org-merge-button"><Merge className="h-4 w-4" /> Merge into…</button>}
            <button className="btn-secondary btn-sm" data-testid="org-download-unused-pdf-button" onClick={() => downloadFile(`/admin/organizations/${id}/unused-codes.pdf`, `unused-codes-${o.name}.pdf`).catch((e) => toast.error(errMsg(e)))}>
              <Download className="h-4 w-4" /> Unused codes PDF
            </button>
            {can("manage_codes") && (
              <button className="btn-primary btn-sm" data-testid="admin-email-unused-codes-pdf-button" disabled={emailing || !d.counts.unused} onClick={emailUnused}>
                {emailing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email unused codes PDF
              </button>
            )}
          </>
        }
      />
      <div className="mb-8 grid grid-cols-2 gap-4 sm:grid-cols-5">
        {COUNT_TONES.map(([k, l, c]) => (
          <div key={k} className="card-soft p-5" data-testid={`org-count-${k}`}>
            <p className="text-sm font-semibold text-[#64748B]">{l}</p>
            <p className={`mt-1 font-mono text-3xl font-semibold ${c}`}>{d.counts[k] || 0}</p>
          </div>
        ))}
      </div>
      <DioceseContacts org={o} canEdit={can("manage_organizations")} onAdded={load} />
      <Tabs defaultValue="codes">
        <TabsList className="mb-5 rounded-xl bg-[#F1EFEA] p-1">
          <TabsTrigger value="codes" className="rounded-lg px-4 font-semibold" data-testid="org-tab-codes">Codes ({d.codes.length})</TabsTrigger>
          <TabsTrigger value="cases" className="rounded-lg px-4 font-semibold" data-testid="org-tab-cases">Applicants ({d.cases.length})</TabsTrigger>
          <TabsTrigger value="orders" className="rounded-lg px-4 font-semibold" data-testid="org-tab-orders">Orders ({d.orders.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="codes"><Card><CodesTable codes={d.codes} onChanged={load} hideOrg /></Card></TabsContent>
        <TabsContent value="cases">
          <Card>
            {d.cases.length ? (
              <table className="tbl">
                <thead><tr><th>Applicant</th><th>Code</th><th>Status</th><th>Submitted</th></tr></thead>
                <tbody>
                  {d.cases.map((c: any) => (
                    <tr key={c.id}>
                      <td><Link to={`/admin/cases/${c.id}`} className="font-semibold text-[#1B365D] hover:underline">{[c.data?.personal?.first_name, c.data?.personal?.last_name].filter(Boolean).join(" ") || "(not entered yet)"}</Link></td>
                      <td className="font-mono text-[13px]">{c.code}</td>
                      <td><StatusBadge status={c.status === "submitted" ? c.case_status : c.status} /></td>
                      <td className="text-xs">{fmtDate(c.submitted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <Empty text="No applicants yet." />}
          </Card>
        </TabsContent>
        <TabsContent value="orders">
          <Card>
            {d.orders.length ? (
              <table className="tbl">
                <thead><tr><th>Order</th><th>Qty</th><th>Amount</th><th>Status</th><th>Placed</th></tr></thead>
                <tbody>
                  {d.orders.map((x: any) => (
                    <tr key={x.id}><td className="font-mono text-[13px]">{x.number}</td><td>{x.quantity}</td><td>{money(x.amount)}</td><td><StatusBadge status={x.status} /></td><td className="text-xs">{fmtDate(x.created_at)}</td></tr>
                  ))}
                </tbody>
              </table>
            ) : <Empty text="No orders yet." />}
          </Card>
        </TabsContent>
      </Tabs>
      <OrgFormDialog open={edit} onOpenChange={setEdit} initial={o} onSaved={load} />
      {merging && <MergeDialog o={o} onClose={() => setMerging(false)} />}
    </div>
  );
}
